/**
 * personnelAdminService.js — İnceleme/admin tarafı kişi (personel) belgesi onay/red akışı.
 * Route: /Management/Firmalar/Personel, /Management/Firmalar/Personel/:firmaId
 *
 * `firmaService.js`'teki `getFirmaDocumentsAdmin`/`reviewFirmaDocument`'in (Firma Profili
 * İncele sayfası) PersonnelController karşılığı — bir denetmenin/adminin herhangi bir
 * firmanın (seçili fuardaki) kadrosunu ve her personelin 5 belgesini (KVKK/SGK/İSG/
 * Sağlık/Mesleki Eğitim) görüp onaylayabileceği/reddedebileceği/beklemeye alabileceği
 * araç. Firma-tarafı `firmaPersonelListService.js`ten KASITLI OLARAK ayrı: o servis
 * mock-uyumlu alan adlarına çeviriyor (personId→id, owner→role vb.), bu servis ham DTO'yu
 * (fileId, reviewStatus, rejectReason, expiryDate dahil) hiç çevirmeden döner — inceleme
 * sayfası bu ham alanlara ihtiyaç duyar. `FirmaProfilIncelemePage.jsx` gibi mock katmanı
 * YOK — inceleme/onay akışının kendisi zaten sadece gerçek backend'e karşı anlamlı, bkz.
 * docs/guides/FE-PersonnelController-guide.md.
 *
 * Reviewer (`file_approval.reviewed_by`) session'dan gelir (`X-user-Id`, getAuthHeader() zaten
 * gönderiyor) — FirmaController'ın `reviewedBy` body alanının aksine burada YOK, backend
 * PATCH gövdesinde göndersen de görmezden gelir (bkz. rehber "The reviewer is taken from the
 * session, never from the body").
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

const BASE = `${JAVA_BACKEND_API_URL}/personnel`;

// Aynı test dekoratörü/fuarı — firmaPersonelListService.js / firmaProfileService.js'teki
// TEST_DECORATOR_FIRMA_ID/TEST_FUAR_ID ile aynı değerler, dosya-yerel kopya (bu servis o
// dosyaları import etmiyor, admin tarafı ayrı) — bkz. FE-PersonnelController-guide.md'nin
// "Frontend-seeded fixture on fuar 298" bölümü: seed edilmiş 4 kişi (t) işaretli.
export const DEFAULT_FIRMA_ID = 49858;
export const DEFAULT_FUAR_ID = 298;

// Backend 400/500'ü bazen düz metin, bazen {message,...} zarfı döner (bkz. rehber "Error
// bodies are inconsistent") — diğer servislerdeki aynı yardımcının kopyası.
function parseError(error) {
  const message =
    typeof error.response?.data === 'string'
      ? error.response.data
      : error.response?.data?.message ?? error.message ?? i18n.t("services:personnelAdminService.bilinmeyenHata");
  throw new Error(message);
}

/**
 * Bir firmanın (seçili fuardaki) kadrosu — her kayıt ham `FirmaPersonelDTO` (fileId,
 * reviewStatus, rejectReason, expiryDate dahil beş belgenin tam hali).
 *
 * `eslesmeDurumu` (0 Bekliyor / 1 Onaylandı / 2 Reddedildi, backend #113) süzer; verilmezse
 * hepsi — eski, kaydı olmayan görevlendirmeler (`eslesmeDurumu` null) YALNIZCA o zaman çıkar.
 * Zarf `eslesmeDurumSayilari` taşır ({bekliyor, onaylandi, reddedildi, kayitYok}); süzgeçten
 * ETKİLENMEZ (aynı arama ve #65 kuralıyla hesaplanır), yani sekme rozetleri tek çağrıdan gelir.
 * GET /api/v1/personnel/firma/{firmaId}?fuarId=&search=&eslesmeDurumu=&page=&size= (0-tabanlı)
 */
export async function getPersonelRosterAdmin(
  firmaId, fuarId, { search = '', page = 1, size = 20, eslesmeDurumu } = {},
) {
  try {
    const params = { fuarId, search, page: page - 1, size };
    if (eslesmeDurumu !== undefined && eslesmeDurumu !== null) params.eslesmeDurumu = eslesmeDurumu;
    const response = await axios.get(`${BASE}/firma/${firmaId}`, {
      params,
      headers: getAuthHeader(),
    });
    const data = response.data; // { records, total, page, size, totalPages, fuarCanli, eslesmeDurumSayilari } — 0-tabanlı
    return {
      total: data.total,
      page: data.page + 1,
      size: data.size,
      totalPages: data.totalPages,
      records: data.records || [],
      eslesmeDurumSayilari: data.eslesmeDurumSayilari ?? null,
    };
  } catch (error) {
    parseError(error);
  }
}

/**
 * Bir kişi belgesini onaylar (1) / reddeder (2, rejectReason zorunlu) / beklemeye alır (0,
 * "reopen" — daha önce onaylanmış/reddedilmiş bir belgeyi tekrar değerlendirmeye açar).
 * `fileId`, roster kaydındaki `documents[key].fileId`'den gelir.
 * ⚠️ Onaylarken (status 1) `gecerlilikTarihi` ("yyyy-MM-dd", bugün ya da sonrası) ZORUNLU — backend #295:
 * firma belgeyi son kullanma tarihsiz yükler, ne zamana kadar sayılacağını onaylayan personel belirler.
 * Tarihsiz onay 400 döner. Red ve beklemeye alma tarihi yok sayar.
 * PATCH /api/v1/personnel/documents/{fileId}/review
 */
export async function reviewPersonelDocument(fileId, { status, rejectReason, gecerlilikTarihi } = {}) {
  try {
    const body = { status };
    if (rejectReason) body.rejectReason = rejectReason;
    if (gecerlilikTarihi) body.gecerlilikTarihi = gecerlilikTarihi;
    const response = await axios.patch(`${BASE}/documents/${fileId}/review`, body, { headers: getAuthHeader() });
    return response.data; // { message, result, data: { fileId, approvalStatus, rejectReason, reviewedBy, insertDate, updateDate } }
  } catch (error) {
    parseError(error);
  }
}

/**
 * Tek bir personelin tam detayı (liste satırıyla birebir aynı ham `FirmaPersonelDTO`) —
 * Personel Detayı sayfasının (`PersonelDetayPage.jsx`) tek kaynağı. `firmaId`+`fuarId`
 * üyelik kontrolü olarak da çalışır (bkz. rehber) — bu firmanın bu fuardaki kadrosunda
 * olmayan bir personId 404 döner.
 * GET /api/v1/personnel/firma/{firmaId}/{personId}?fuarId=
 */
export async function getPersonelDetayAdmin(firmaId, personId, fuarId) {
  try {
    const response = await axios.get(`${BASE}/firma/${firmaId}/${personId}`, {
      params: { fuarId },
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * #549 / #550 — may the person card be deleted, i.e. does nothing point at it? `{ message, data: { silinebilir, engeller: [{ tur, sayi,
 * metin }] } }`. page.kisi (403 without it), 404 no such person. The error is NOT run through parseError: KontrolluSilDugmesi reads
 * the status (403 hides the button) and the 409 body.
 * GET /api/v1/personnel/{personId}/kalici-silme-kontrolu
 */
export async function getKisiSilmeKontrolu(personId) {
  const response = await axios.get(`${BASE}/${personId}/kalici-silme-kontrolu`, { headers: getAuthHeader() });
  return response.data;
}

/**
 * #549 / #550 — deletes a person card PERMANENTLY, only a card nothing points at (the row is archived to deleted_records first).
 * 409 with the same data as getKisiSilmeKontrolu otherwise. NOT `DELETE /personnel/kartsil/{id}`, which deletes a person WITH history.
 * DELETE /api/v1/personnel/{personId}/kalici-sil
 */
export async function kaliciSilKisi(personId) {
  const response = await axios.delete(`${BASE}/${personId}/kalici-sil`, { headers: getAuthHeader() });
  return response.data;
}

/**
 * Staff Kişi Detay'ın TEK okuması (backlog #364 / #365, backend 2026-10-13): firma bağlamı gerekmez, hiç
 * görevlendirilmemiş kişi de açılır. `fuarId` üst çubuktaki fuardır ve isteğe bağlıdır; verilmezse yaş kuralı,
 * kart kararı ve firma blokları yoktur (`firmalar: []`).
 * { personId, fuarId, birlestirildigiPersonId, kartKapsami, fuardaGorevli, kartFirmaId, belgeIsteyenFirmalar,
 *   kisi (Kişi Listesi satırı + bilgiler / kimlikDogrulama / fotografYukleme), firmalar[] (kart-karari girdileri) }
 * Birleştirilmiş (gizli) kart 404 değil 200 döner, `birlestirildigiPersonId` dolu — sayfa salt okunur gösterir.
 * GET /api/v1/personnel/{personId}/detay?fuarId=
 */
export async function getKisiDetay(personId, fuarId) {
  try {
    const params = {};
    if (fuarId) params.fuarId = fuarId;
    const response = await axios.get(`${BASE}/${personId}/detay`, { params, headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Yeni bir kişi (`person`) kaydı oluşturur — henüz hiçbir firmaya/fuara bağlı değildir,
 * bkz. rehberin "does not attach them to any firma or fair" notu. `data` yeni `personId`'dir.
 * `person_logo` göndermiyoruz (mock — bkz. rehber "The logo is silently dropped"), bu yüzden
 * multipart yerine düz form-urlencoded yeterli.
 *
 * #288 (backend #287): aynı ad-soyad + kimlik no TEK hane farklı bir kart varsa uç 409 döner ve kişiyi
 * OLUŞTURMAZ; gövde `data: { kod: 'BENZER_KAYIT', adaylar[] }`. Bu durumda fırlatılan hata `kod` ve
 * `adaylar` taşır (dallanma mesaja değil `kod`a göre). `benzerKayitOnaylandi: true` = "adayları gördüm,
 * farklı kişi" → aynı form bu kez oluşturulur.
 * POST /api/v1/personnel/yenikart
 */
export async function createPersonAdmin({
  idNumber, name, surname, nationalityId = 1, phone, email, givenVest = 0, benzerKayitOnaylandi = false,
}) {
  try {
    const form = new URLSearchParams();
    form.set('person_id_number', idNumber);
    form.set('person_name', name);
    form.set('person_surname', surname);
    form.set('person_nationality_id', String(nationalityId));
    form.set('person_given_vest', String(givenVest));
    if (phone) form.set('person_phone', phone);
    if (email) form.set('person_email', email);
    if (benzerKayitOnaylandi) form.set('benzerKayitOnaylandi', 'true');
    const response = await axios.post(`${BASE}/yenikart`, form, {
      headers: { ...getAuthHeader(), 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data.data; // yeni person_id
  } catch (error) {
    const govde = error.response?.data?.data;
    if (error.response?.status === 409 && govde?.kod === 'BENZER_KAYIT') {
      const benzer = new Error(error.response.data.message || i18n.t("services:personnelAdminService.benzerBirKisiKaydiVar"));
      benzer.status = 409;
      benzer.kod = 'BENZER_KAYIT';
      benzer.adaylar = Array.isArray(govde.adaylar) ? govde.adaylar : [];
      throw benzer;
    }
    parseError(error);
  }
}

/**
 * Bir kişinin firmaları (güncel + geçmiş, sigortalayan önde). YALNIZCA SAKA personeli — firma hesabı 403.
 * Her firma: `iliskiDurumu` (AKTIF / KAYITSIZ / SONLANDIRILDI / YOK), `guncel`, son fuar alanları
 * (`sonFuarTarihi`) ve fuar başına bir `gorevler[]` ({ fuarId, eslesmeDurumu(Adi), meslekAdi, … }).
 * "Fuara Eşle" (#261) bununla firmanın zaten bu fuarda olup olmadığını ve ön dolum firmasını bulur.
 * GET /api/v1/personnel/{personId}/firmalar
 */
export async function getKisiFirmalari(personId) {
  try {
    const response = await axios.get(`${BASE}/${personId}/firmalar`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Var olan bir kişiyi bir firmaya `fuarId` fuarında ekler/eşler. Idempotent: ikinci çağrı meslek_id'yi
 * günceller, satırı çoğaltmaz.
 * #387 (backend #387): fuar gövdede `fuar_id` olarak gider; verilmezse backend kullanıcının SEÇİLİ fuarını
 * (user.fuar_id, üst çubuk) veritabanından okur. Firma girişi yalnızca firmasının fuarlarını gönderebilir (403).
 * WAS (until #387): "session'ın aktif fuarında (bypass altında `X-fuar-id` header'ı, bkz. rehberin "unique to
 * this controller" bölümü)" — oturumun fuarı GİRİŞ anındakiydi, üst çubukta fuar değişince takip etmiyordu.
 *
 * ⚠️⚠️ ÖN KOŞUL (2026-09-03): kişi firmanın AKTİF personel listesinde (`person_to_firma`,
 * `aktif = 1`) değilse bu uç **409** döner; SAKA personeli için de öyle. Backend #112'den
 * (2026-09-29) beri `durum`/onay OKUNMUYOR — yalnızca aktif satır aranıyor, mesajda da
 * artık "onayı bekliyor" geçmiyor. `person_to_firma` boş başladı ve geri doldurulmadı, yani
 * bu ön koşul nadir bir istisna değil, HER görevlendirmenin ilk adımı.
 *
 * Bu yüzden önce `personelRosterService.addToRoster` çağrılır (idempotent, anında onaylı) ve
 * ancak sonra bu uç; Components/Kisi/PersonelEkleModal zinciri böyle kurulu. 409 yine de
 * gelirse (satır arada sonlandırılmış olabilir) mesajı olduğu gibi gösterin.
 *
 * `data` yeni bir görevlendirmede `eslesme_tipi`dir (1 Görevlendirme / 2 Eşleme,
 * oturumun kullanıcı grubundan TÜRETİLİR, gövdeden gönderilemez); "zaten eşlenmiş"
 * dalında `null` gelir.
 *
 * #372 (backend #371): `meslekIds` birden çok meslek verir (`meslek_ids`, `meslek_id`yi geçersiz kılar); var olan bir işe
 * alımda dolu küme TÜM kümeyi değiştirir, boş küme dokunmaz. `meslekId` tek elemanlı küme sayılır — yine de var olan bir
 * işe alıma tek `meslekId` yollamak ikinci mesleği düşürür; çağıran kümeyi bütün olarak göndermeli.
 * POST /api/v1/personnel/yeni
 */
export async function assignPersonToFirmaAdmin({ personId, firmaId, meslekId = 0, meslekIds = null }, fuarId) {
  try {
    const body = { person_id: personId, firma_id: firmaId, meslek_id: meslekId };
    if (Array.isArray(meslekIds)) {
      const ids = meslekIds.map(Number).filter((id) => id > 0);
      body.meslek_ids = ids;
      body.meslek_id = ids[0] ?? 0;
    }
    if (Number(fuarId) > 0) body.fuar_id = Number(fuarId);   // #387
    const response = await axios.post(
      `${BASE}/yeni`,
      body,
      // WAS (until #387): { headers: { ...getAuthHeader(), 'X-fuar-id': fuarId } },
      { headers: getAuthHeader() },
    );
    return response.data; // { message, result, data: eslesmeTipi | null }
  } catch (error) {
    parseError(error);
  }
}

/**
 * Kişiyi bir fuarın KADROSUNDAN çıkarır (`person_firma_to_fuar`). Firmanın DAİMÎ personel
 * listesine (`person_to_firma`) dokunmaz — o ayrı bir çağrı
 * (`personelRosterService.removeFromRoster`). Tam çıkış ikisi birden.
 *
 * ⚠️ `fuarId` ZORUNLU (query param) — kadro kaydı fuar başınadır.
 * ⚠️ `firmaId` opsiyonel ve anlamı büyük: VERİLMEZSE kişi o fuarda HANGİ FİRMADA varsa
 * hepsinden düşer ("bu kişi artık alanda değil"). Bu biçim yalnızca SAKA personeline
 * açık. Bir firmadan çıkarmak için firmaId MUTLAKA verilmeli.
 * ⚠️ Satırlar silinmeden önce `deleted_records`'a arşivleniyor, yani geri alınabilir bir
 * iz kalıyor — ama bu ekrandan geri alınamaz.
 * DELETE /api/v1/personnel/sil/{personId}?fuarId=&firmaId=
 */
export async function removeFromFuarCrew(personId, { fuarId, firmaId } = {}) {
  try {
    const params = { fuarId };
    if (firmaId) params.firmaId = firmaId;
    const response = await axios.delete(`${BASE}/sil/${personId}`, {
      params,
      headers: getAuthHeader(),
    });
    // { message, result, data: kalanFirmaSayisi | null }
    return response.data?.message || i18n.t("services:personnelAdminService.personelFuardanCikarildi");
  } catch (error) {
    parseError(error);
  }
}
